import { HTTP_STATUS_CODE, post } from '@api/api';
import API_ROUTES from '@api/routes';
import ConnectorEditDialog from '@components/ConnectorEdit/ConnectorEditDialog';
import {
	getTestServicePayload,
	getConnectorConfigs,
	getServiceId,
	getServiceProviderKey,
	getUpdatedConnectors,
	removeConnectorItem,
	withDefaultConnector
} from '@utils/connectors';
import { AuthMethodRoute } from 'constants/routes';
import { last, noop, omit } from 'lodash';
import React, {
	createContext,
	FC,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import { IChildrenProps } from 'types/common';
import {
	ConnectorActions,
	ConnectorIndexEnum,
	ConnectorModelData,
	ConnectorTypeEnum,
	CustomAuthConnector,
	CustomTemplateProps,
	DEFAULT_CONNECTOR_ID,
	IConnectorDefaults,
	IConnectors,
	ICustomFormProps,
	IDefaultConnectorProps,
	IDefaultPasswordProps,
	IEmailCustomTemplate,
	IErrorMessage,
	IFormikValues,
	ISmsCustomTemplate,
	ITestServicePayloadMethodProps,
	ProviderPrefix
} from 'types/connectors';
import { ToastSeverity } from 'types/toast';
import useToast from './useToast';

interface IConnectorContext {
	editing: boolean;
	emailConnectors: () => CustomAuthConnector[];
	smsConnectors: () => CustomAuthConnector[];
	getCustomTemplates: (
		type: ConnectorTypeEnum,
		resetMethod?: AuthMethodRoute
	) => any;
	setEditing: React.Dispatch<React.SetStateAction<boolean>>;
	createConnector: (type: ConnectorIndexEnum, formik: IFormikValues) => void;
	editConnector: (
		connector: CustomAuthConnector,
		formik: IFormikValues
	) => void;
	updateCustomTemplates: (
		props: ICustomFormProps,
		resetMethod?: AuthMethodRoute
	) => any;
	setConnectors: React.Dispatch<React.SetStateAction<IConnectors | undefined>>;
	onTestMessageService: (
		props: ITestServicePayloadMethodProps
	) => Promise<void>;
}

interface IMessageConnectorProviderProps extends IChildrenProps {
	pageId: AuthMethodRoute;
	customConnectors: IConnectors;
	defaultTemplates: IConnectorDefaults;
	onSaveConnectors: (data: IConnectors) => Promise<IConnectors>;
}

export const MessageConnectorContext = createContext<IConnectorContext>({
	editing: false,
	emailConnectors: () => [],
	smsConnectors: () => [],
	createConnector: noop,
	editConnector: noop,
	setEditing: noop,
	getCustomTemplates: () => {},
	updateCustomTemplates: () => {},
	setConnectors: noop,
	onTestMessageService: async () => {}
});

export const MessageConnectorProvider: FC<IMessageConnectorProviderProps> = ({
	pageId,
	customConnectors,
	defaultTemplates,
	children,
	onSaveConnectors
}) => {
	const { addToast } = useToast();

	const [connectors, setConnectors] = useState<IConnectors>();
	const [editing, setEditing] = useState(false);
	const [currentConnector, setCurrentConnector] =
		useState<CustomAuthConnector | null>(null);
	const [currentForm, setCurrentForm] = useState<IFormikValues>({
		setFieldValue: noop,
		values: {}
	});

	const emailConnectors = useCallback(
		(): CustomAuthConnector[] =>
			withDefaultConnector(
				ProviderPrefix.EMAIL,
				getConnectorConfigs(connectors).emailProviders
			),
		[connectors]
	);

	const smsConnectors = useCallback(
		(): CustomAuthConnector[] =>
			withDefaultConnector(
				ProviderPrefix.SMS,
				getConnectorConfigs(connectors).smsProviders
			),
		[connectors]
	);

	const createConnector = (type: ConnectorIndexEnum, formik: IFormikValues) => {
		setCurrentForm(formik);
		setCurrentConnector(ConnectorModelData[type]);
		setEditing(true);
	};

	const editConnector = (
		connector: CustomAuthConnector,
		formik: IFormikValues
	) => {
		setCurrentForm(formik);
		setCurrentConnector(connector);
		setEditing(true);
	};

	const onEditComplete = () => {
		setEditing(false);
		setCurrentConnector(null);
	};

	const updateCustomTemplates = useCallback(
		(props: ICustomFormProps, resetMethod?: AuthMethodRoute) => {
			const updatedConnectors = connectors;
			const { customEmailBody, customEmailSubject, customTextMessageBody } =
				props;
			const keys = Object.keys(props);

			if (
				keys.includes(CustomTemplateProps.EMAIL_SUBJECT) &&
				keys.includes(CustomTemplateProps.EMAIL_BODY)
			) {
				const tpls = resetMethod
					? ((defaultTemplates[pageId] as IDefaultPasswordProps)[
							resetMethod as AuthMethodRoute
					  ] as IDefaultConnectorProps)
					: (defaultTemplates[pageId] as IDefaultConnectorProps);

				const customEmailTemplates: IEmailCustomTemplate = {
					emailBody: tpls.emailBody as string,
					emailSubject: tpls.emailSubject as string
				};
				customEmailTemplates.emailBody = customEmailBody || '';
				customEmailTemplates.emailSubject = customEmailSubject || '';

				if (resetMethod) {
					(updatedConnectors!.emailProviders.customTemplates[pageId] as any)[
						resetMethod
					] = customEmailTemplates as any;
				} else {
					updatedConnectors!.emailProviders.customTemplates[pageId] =
						customEmailTemplates as any;
				}
			}

			if (keys.includes(CustomTemplateProps.TEXT_BODY)) {
				const customSmsTemplates: ISmsCustomTemplate = {
					textMessageBody: (defaultTemplates[pageId] as IDefaultConnectorProps)
						.textBody as string
				};
				customSmsTemplates.textMessageBody = customTextMessageBody || '';
				updatedConnectors!.smsProviders.customTemplates[pageId] =
					customSmsTemplates as any;
			}

			return updatedConnectors;
		},
		[connectors, defaultTemplates, pageId]
	);

	const onConnectorUpdate = async (connector: CustomAuthConnector) => {
		const payload = getUpdatedConnectors(connector, connectors as IConnectors);
		const isCreate = connector.common?.id ?? ConnectorActions.CREATE;

		await onSaveConnectors(payload)
			.then((res) => {
				setConnectors(res);
				return res;
			})
			.then((data: IConnectors) => {
				if (isCreate) {
					const { providerType, providerProtocol } = connector;
					const res = last(data[providerType][providerProtocol]);
					currentForm.setFieldValue(
						getServiceProviderKey(providerType),
						getServiceId(providerProtocol, res!.common.id)
					);
				}
			})
			.finally(() => onEditComplete());
	};

	const onConnectorDelete = async (connector: CustomAuthConnector) => {
		const { providerType, providerProtocol } = connector;
		const payload = removeConnectorItem(
			connector,
			connectors as IConnectors,
			defaultTemplates[pageId] as IDefaultConnectorProps,
			pageId
		);

		await onSaveConnectors({ ...payload })
			.then((res) => {
				setConnectors(res);

				const providerFieldName = getServiceProviderKey(providerType);
				if (
					currentForm.values[providerFieldName] !== DEFAULT_CONNECTOR_ID &&
					currentForm.values[providerFieldName] ===
						getServiceId(providerProtocol, connector.common.id)
				) {
					currentForm.setFieldValue(
						getServiceProviderKey(providerType),
						DEFAULT_CONNECTOR_ID
					);
				}
			})
			.then(() =>
				addToast({
					severity: ToastSeverity.Success,
					content: intl.tr('connectors.delete.toast.success'),
					id: 'submit-form-success'
				})
			)
			.catch(() =>
				addToast({
					severity: ToastSeverity.Error,
					content: intl.tr('connectors.delete.toast.error'),
					id: 'submit-form-error'
				})
			)
			.finally(() => onEditComplete());
	};

	const getCustomTemplates = useCallback(
		(type: ConnectorTypeEnum, resetMethod?: AuthMethodRoute) => {
			if (resetMethod) {
				try {
					return (
						(connectors as IConnectors)[type].customTemplates[pageId] as any
					)[resetMethod];
				} catch {
					return {};
				}
			}

			try {
				return (connectors as IConnectors)[type].customTemplates[pageId];
			} catch {
				return {};
			}
		},
		[connectors, pageId]
	);

	const onTestMessageService = useCallback(
		async (props: ITestServicePayloadMethodProps) => {
			const { isAdhoc, onComplete, onFail } = props;
			const payload = getTestServicePayload(omit(props, ['onFail']));
			const route = isAdhoc
				? API_ROUTES.v1.messaging.adhoc
				: API_ROUTES.v1.messaging.service;
			await post<any>(route, payload)
				.then((res) => {
					if (res.status === HTTP_STATUS_CODE.ok) {
						onComplete();
					}
				})
				.catch((err) => {
					onFail(err.response.data as IErrorMessage);
				});
		},
		[]
	);

	useEffect(
		() => setConnectors(customConnectors),
		[customConnectors, pageId, onSaveConnectors]
	);

	const context = useMemo<IConnectorContext>(
		() => ({
			editing,
			createConnector,
			editConnector,
			setEditing,
			emailConnectors,
			smsConnectors,
			getCustomTemplates,
			updateCustomTemplates,
			setConnectors,
			onTestMessageService
		}),
		[
			editing,
			getCustomTemplates,
			emailConnectors,
			smsConnectors,
			setConnectors,
			updateCustomTemplates,
			onTestMessageService
		]
	);

	return (
		<MessageConnectorContext.Provider value={context}>
			{children}
			{editing && (
				<ConnectorEditDialog
					open={editing}
					pageId={pageId}
					connector={currentConnector!}
					onSave={onConnectorUpdate}
					onDelete={onConnectorDelete}
					onClose={onEditComplete}
					onTest={onTestMessageService}
				/>
			)}
		</MessageConnectorContext.Provider>
	);
};
